/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – UsersPanel.tsx FINAL
// Adăugat: Search/filter utilizatori, reset parolă real
// ============================================================

import React, { useState, useMemo } from 'react';
import { Form, Input, Select, Switch, Modal, message, Popconfirm } from 'antd';
import {
  UserOutlined, LockOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined,
  TeamOutlined, SafetyCertificateOutlined, EyeInvisibleOutlined, KeyOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { EyeTwoTone } from '@ant-design/icons';

import { useAuth } from '../../context/AuthContext';
import { AppUser, UserRole } from '../../types';

const { Option } = Select;

// ─── Constante ────────────────────────────────────────────

const ROLE_LABEL: Record<UserRole, string> = {
  admin:    'Administrator',
  operator: 'Operator',
  viewer:   'Vizualizator',
};
const ROLE_COLOR: Record<UserRole, string> = {
  admin: '#6366f1', operator: '#10b981', viewer: '#06b6d4',
};
const ROLE_BG: Record<UserRole, string> = {
  admin: '#eef2ff', operator: '#d1fae5', viewer: '#ecfeff',
};
const ROLE_ICON: Record<UserRole, React.ReactNode> = {
  admin:    <SafetyCertificateOutlined />,
  operator: <UserOutlined />,
  viewer:   <CheckCircleOutlined />,
};
const ROLE_PERMS: Record<UserRole, string[]> = {
  admin:    ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate', 'Mentenanță', 'Configurare', 'Utilizatori'],
  operator: ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate', 'Mentenanță', 'Configurare'],
  viewer:   ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate'],
};

// ─── User Card ────────────────────────────────────────────

const UserCard: React.FC<{
  user: AppUser; isCurrent: boolean;
  onEdit: () => void; onReset: () => void;
  onToggle: () => void; onDelete: () => void;
}> = ({ user: u, isCurrent, onEdit, onReset, onToggle, onDelete }) => {
  const color = ROLE_COLOR[u.role];

  return (
    <div style={{
      background: '#fff', borderRadius: 14,
      border: `1px solid ${isCurrent ? color + '40' : '#e2e8f0'}`,
      padding: '18px', boxShadow: isCurrent ? `0 0 0 2px ${color}20` : '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${color}99)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
          {u.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
            {isCurrent && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: `${color}15`, color, flexShrink: 0 }}>TU</span>}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
        </div>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: u.active ? '#10b981' : '#ef4444', flexShrink: 0, marginTop: 2 }} />
      </div>

      {/* Role + status */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: ROLE_BG[u.role], color, display: 'flex', alignItems: 'center', gap: 4 }}>
          {ROLE_ICON[u.role]} {ROLE_LABEL[u.role]}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: u.active ? '#d1fae5' : '#fee2e2', color: u.active ? '#059669' : '#ef4444' }}>
          {u.active ? 'Activ' : 'Dezactivat'}
        </span>
      </div>

      {/* Last login */}
      {u.lastLogin && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 12 }}>
          Ultima conectare: {new Date(u.lastLogin).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      {/* Actions */}
      {!isCurrent && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={onEdit} style={{ flex: 1, padding: '6px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <EditOutlined /> Editează
          </button>
          <button onClick={onReset} style={{ flex: 1, padding: '6px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #fde68a', background: '#fefce8', color: '#d97706', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <KeyOutlined /> Parolă
          </button>
          <button onClick={onToggle} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: `1px solid ${u.active ? '#fecaca' : '#bbf7d0'}`, background: u.active ? '#fff5f5' : '#f0fdf4', color: u.active ? '#ef4444' : '#059669', cursor: 'pointer', fontFamily: 'inherit' }}>
            {u.active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          </button>
          <Popconfirm title={`Ștergi contul ${u.name}?`} onConfirm={onDelete} okText="Șterge" cancelText="Nu" okButtonProps={{ danger: true }}>
            <button style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #fecaca', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit' }}>
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
};

// ─── Modal add/edit ───────────────────────────────────────

const UserModal: React.FC<{
  open: boolean; editing: AppUser | null;
  onClose: () => void; onSave: (data: any, isEdit: boolean) => void;
}> = ({ open, editing, onClose, onSave }) => {
  const [form] = Form.useForm();

  const handleOk = () => {
    form.validateFields().then(vals => { onSave(vals, !!editing); form.resetFields(); });
  };

  return (
    <Modal
      open={open} onCancel={() => { onClose(); form.resetFields(); }} onOk={handleOk}
      title={editing ? `Editează — ${editing.name}` : 'Utilizator nou'}
      okText="Salvează" cancelText="Anulează" width={460}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}
        initialValues={editing ? { name: editing.name, email: editing.email, role: editing.role, active: editing.active, phone: editing.phone } : { role: 'viewer', active: true }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="name" label="Nume" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined style={{ color: '#94a3b8' }} />} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
        </div>
        {!editing && (
          <Form.Item name="password" label="Parolă" rules={[{ required: true }, { min: 6, message: 'Minim 6 caractere.' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              iconRender={v => v ? <EyeTwoTone twoToneColor="#6366f1" /> : <EyeInvisibleOutlined />} />
          </Form.Item>
        )}
        <Form.Item name="phone" label="Telefon">
          <Input prefix="📱" />
        </Form.Item>
        <Form.Item name="role" label="Rol" rules={[{ required: true }]}>
          <Select optionLabelProp="label">
            {(Object.keys(ROLE_LABEL) as UserRole[]).map(r => (
              <Option key={r} value={r} label={ROLE_LABEL[r]}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: ROLE_BG[r], color: ROLE_COLOR[r], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{ROLE_ICON[r]}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{ROLE_LABEL[r]}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{ROLE_PERMS[r].length} secțiuni</div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(p, c) => p.role !== c.role}>
          {({ getFieldValue }) => {
            const role = getFieldValue('role') as UserRole;
            if (!role) return null;
            return (
              <div style={{ padding: '10px 14px', borderRadius: 9, background: ROLE_BG[role], border: `1px solid ${ROLE_COLOR[role]}30`, marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: ROLE_COLOR[role], marginBottom: 6 }}>Acces — {ROLE_LABEL[role]}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {ROLE_PERMS[role].map(p => <span key={p} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: '#fff', color: ROLE_COLOR[role], border: `1px solid ${ROLE_COLOR[role]}30` }}>{p}</span>)}
                </div>
              </div>
            );
          }}
        </Form.Item>
        <Form.Item name="active" label="Cont activ" valuePropName="checked">
          <Switch checkedChildren="Activ" unCheckedChildren="Dezactivat" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Modal reset parolă ───────────────────────────────────

const ResetPwModal: React.FC<{ user: AppUser | null; onClose: () => void; onReset: (userId: string, newPw: string) => void }> =
  ({ user: u, onClose, onReset }) => {
  const [form] = Form.useForm();
  const handleOk = () => form.validateFields().then(vals => {
    onReset(u!.id, vals.pw);
    message.success(`Parola pentru ${u?.name} a fost resetată.`);
    form.resetFields();
    onClose();
  });

  return (
    <Modal open={!!u} onCancel={() => { onClose(); form.resetFields(); }} onOk={handleOk}
      okText="Resetează" cancelText="Anulează" okButtonProps={{ style: { background: '#f59e0b', borderColor: '#f59e0b' } }}
      title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><KeyOutlined style={{ color: '#d97706' }} /> Resetare parolă — {u?.name}</div>}
      width={380}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="pw" label="Parolă nouă" rules={[{ required: true }, { min: 6, message: 'Minim 6 caractere' }]}>
          <Input.Password prefix={<LockOutlined style={{ color: '#94a3b8' }} />} iconRender={v => v ? <EyeTwoTone twoToneColor="#6366f1" /> : <EyeInvisibleOutlined />} />
        </Form.Item>
        <Form.Item name="pw2" label="Confirmă" dependencies={['pw']} rules={[{ required: true }, ({ getFieldValue }) => ({ validator(_, v) { return !v || getFieldValue('pw') === v ? Promise.resolve() : Promise.reject('Parolele nu coincid.'); } })]}>
          <Input.Password prefix={<LockOutlined style={{ color: '#94a3b8' }} />} iconRender={v => v ? <EyeTwoTone twoToneColor="#6366f1" /> : <EyeInvisibleOutlined />} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const UsersPanel: React.FC = () => {
  const { allUsers, addUser, updateUser, deleteUser, user: me, logActivity } = useAuth();

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState<AppUser | null>(null);
  const [resetUser,  setResetUser]  = useState<AppUser | null>(null);
  const [search,     setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');

  const filtered = useMemo(() =>
    allUsers.filter(u => {
      const matchSearch = search === '' ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      return matchSearch && matchRole;
    }),
    [allUsers, search, roleFilter]
  );

  const handleSave = (data: any, isEdit: boolean) => {
    if (isEdit && editing) {
      updateUser(editing.id, { name: data.name, email: data.email, role: data.role, active: data.active, phone: data.phone });
      logActivity(`Utilizator editat: ${data.name}`, 'Utilizatori');
      message.success('Utilizator actualizat!');
    } else {
      addUser({ ...data, active: data.active ?? true });
      message.success('Utilizator adăugat!');
    }
    setModalOpen(false); setEditing(null);
  };

  const handleToggle = (u: AppUser) => {
    updateUser(u.id, { active: !u.active });
    logActivity(`Cont ${u.active ? 'dezactivat' : 'activat'}: ${u.name}`, 'Utilizatori');
    message.success(`Contul ${u.name} a fost ${u.active ? 'dezactivat' : 'activat'}.`);
  };

  const handleDelete = (u: AppUser) => {
    deleteUser(u.id);
    message.success(`Utilizatorul ${u.name} a fost șters.`);
  };

  const handleReset = (userId: string, newPw: string) => {
    const users = (() => { try { return JSON.parse(localStorage.getItem('simadca_users_v3') ?? '[]'); } catch { return []; } })();
    const updated = users.map((u: any) => u.id === userId ? { ...u, password: newPw } : u);
    try { localStorage.setItem('simadca_users_v3', JSON.stringify(updated)); } catch {}
    logActivity(`Parolă resetată pentru utilizatorul ${userId}`, 'Utilizatori');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Utilizatori</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{allUsers.length} conturi · {allUsers.filter(u => u.active).length} active</span>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} style={{ padding: '9px 18px', borderRadius: 10, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <PlusOutlined /> Utilizator nou
        </button>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {(['admin', 'operator', 'viewer'] as UserRole[]).map(r => (
          <div key={r} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.15s', borderColor: roleFilter === r ? ROLE_COLOR[r] : '#e2e8f0' }}
            onClick={() => setRoleFilter(roleFilter === r ? 'all' : r)}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: ROLE_BG[r], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: ROLE_COLOR[r] }}>{ROLE_ICON[r]}</div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{ROLE_LABEL[r]}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: ROLE_COLOR[r] }}>{allUsers.filter(u => u.role === r).length}</div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Input
          placeholder="Caută după nume sau email…"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={search} onChange={e => setSearch(e.target.value)}
          allowClear style={{ maxWidth: 300 }}
        />
        <Select value={roleFilter} onChange={setRoleFilter} style={{ width: 180 }}>
          <Option value="all">Toate rolurile</Option>
          {(Object.keys(ROLE_LABEL) as UserRole[]).map(r => <Option key={r} value={r}>{ROLE_LABEL[r]}</Option>)}
        </Select>
        {(search || roleFilter !== 'all') && (
          <button onClick={() => { setSearch(''); setRoleFilter('all'); }} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 11, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
            Resetează filtre
          </button>
        )}
        <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center', marginLeft: 'auto' }}>{filtered.length} rezultate</span>
      </div>

      {/* Grid utilizatori */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {filtered.map(u => (
          <UserCard key={u.id} user={u} isCurrent={u.id === me?.id}
            onEdit={() => { setEditing(u); setModalOpen(true); }}
            onReset={() => setResetUser(u)}
            onToggle={() => handleToggle(u)}
            onDelete={() => handleDelete(u)}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          <TeamOutlined style={{ fontSize: 32, display: 'block', marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>Niciun utilizator găsit</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Încearcă alte criterii de căutare</div>
        </div>
      )}

      <UserModal open={modalOpen} editing={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSave={handleSave} />
      <ResetPwModal user={resetUser} onClose={() => setResetUser(null)} onReset={handleReset} />
    </div>
  );
};

export default UsersPanel;